import { appApiRoutes } from '@riskbase/types/http/appApi';
import { isError } from 'lib/isError';
import { randomKey } from 'lib/riskbase';
import { requestRoute } from 'service/api';
import { Api } from 'service/api/constants';
import {
  ALERT_ERROR,
  HIDE_BLOCKING_LOADER,
  NAVIGATE_MODAL,
  NAVIGATE_MODAL_BACK,
  SHOW_BLOCKING_LOADER,
} from 'store/actions';
import {
  put,
  race,
  select,
  take,
  takeLatest,
  takeLeading,
} from 'typed-redux-saga';
import { ModalScreens } from 'components/Navigation/types';

import {
  doTasksAssignedFilterOpenModal,
  doTasksAssignedFilterOptionsLoad,
  doTasksAssignedFilterOptionsLoadFailure,
  doTasksAssignedFilterOptionsLoadSuccess,
  doTaskAssignedDetailsLoadFailure,
  doTaskAssignedDetailsLoadSuccess,
  doTasksAssignedDetailsLoad,
  doTasksAssignedLoad,
  doTasksAssignedLoadFailure,
  doTasksAssignedLoadSuccess,
  doTasksAssignedSetFilter,
} from './actions';
import {
  selectTasksAssignedDraftFilterOptions,
  selectTasksAssignedFilter,
  selectTasksAssignedFilterOptionsLoadStatus,
} from './selectors';

function* loadTasks(action: ReturnType<typeof doTasksAssignedLoad>): Generator {
  const { filter: newFilter, showBlockingLoader } = action.payload;
  const hasNewFilter = !!newFilter;
  const currentFilter = yield* select(selectTasksAssignedFilter);
  const filter = { ...currentFilter, ...newFilter };

  if (showBlockingLoader) {
    yield* put(SHOW_BLOCKING_LOADER());
  }
  try {
    const response = yield* requestRoute({
      api: Api.APP,
      route: appApiRoutes.tasks.get,
      query: {
        organisations: filter.organisations,
        properties: filter.properties,
        search: filter.search || undefined,
      },
    });

    // Put results into store
    yield* put(
      doTasksAssignedLoadSuccess({
        tasks: response.data,
        dueGroups: response.included.dueGroups,
      }),
    );

    // Save filter if it's new
    if (hasNewFilter) {
      yield* put(doTasksAssignedSetFilter(filter));
    }

    // If draft filter options exist, save them as non-draft
    const draftFilterOptions = yield* select(
      selectTasksAssignedDraftFilterOptions,
    );
    if (draftFilterOptions) {
      yield* put(
        doTasksAssignedFilterOptionsLoadSuccess({
          filterOptions: draftFilterOptions,
        }),
      );
    }

    // Close filter modal (if it's open)
    yield* put(NAVIGATE_MODAL_BACK([ModalScreens.TasksAssignedFilter]));
  } catch (error) {
    if (isError(error)) {
      if (showBlockingLoader) {
        yield* put(ALERT_ERROR({ error }));
      } else {
        yield* put(
          doTasksAssignedLoadFailure({
            error: error.message,
          }),
        );
      }
    }
  }
  if (showBlockingLoader) {
    yield* put(HIDE_BLOCKING_LOADER());
  }
}

function* loadTaskFilterOptions(
  action: ReturnType<typeof doTasksAssignedFilterOptionsLoad>,
): Generator {
  // This is mimicking whats done in doAsesListFilter
  const blockingLoaderKey = action.payload.showBlockingLoader
    ? randomKey()
    : undefined;
  try {
    if (blockingLoaderKey) {
      yield* put(SHOW_BLOCKING_LOADER(blockingLoaderKey));
    }
    const { organisationIds, draft } = action.payload;
    const response = yield* requestRoute({
      api: Api.APP,
      route: appApiRoutes.tasks.getFilterOptions,
      query: organisationIds?.length ? { organisationIds } : {},
    });

    yield* put(
      doTasksAssignedFilterOptionsLoadSuccess({
        filterOptions: response,
        draft,
      }),
    );
    if (action.payload.loadTasksOnSuccess && !response.organisationRequired) {
      yield* put(doTasksAssignedLoad({}));
    }
  } catch (error) {
    const finalError = isError(error)
      ? error
      : new Error('Unable to load task filter options');

    yield* put(doTasksAssignedFilterOptionsLoadFailure({ error: finalError }));
    if (action.payload.showError) {
      yield* put(ALERT_ERROR({ error: finalError }));
    }
  } finally {
    if (blockingLoaderKey) {
      yield* put(HIDE_BLOCKING_LOADER(blockingLoaderKey));
    }
  }
}

function* openTaskFilterModal(): Generator {
  const filterOptionsLoadStatus = yield* select(
    selectTasksAssignedFilterOptionsLoadStatus,
  );

  if (filterOptionsLoadStatus !== 'loaded') {
    yield* put(SHOW_BLOCKING_LOADER());
    if (filterOptionsLoadStatus !== 'loading') {
      yield* put(doTasksAssignedFilterOptionsLoad({}));
    }
    const taken = yield* race({
      fail: take(doTasksAssignedFilterOptionsLoadFailure),
      done: take(doTasksAssignedFilterOptionsLoadSuccess),
    });
    yield* put(HIDE_BLOCKING_LOADER());
    if (taken.fail) {
      yield* put(ALERT_ERROR({ error: taken.fail.payload.error }));
      return;
    }
  }

  yield* put(
    NAVIGATE_MODAL({
      screen: ModalScreens.TasksAssignedFilter,
      size: 'small',
      params: {},
    }),
  );
}
function* loadTaskDetails(
  action: ReturnType<typeof doTasksAssignedDetailsLoad>,
): Generator {
  const { taskId } = action.payload;
  try {
    const response = yield* requestRoute({
      api: Api.APP,
      route: appApiRoutes.tasks.getDetails,
      params: { id: taskId },
    });

    yield* put(doTaskAssignedDetailsLoadSuccess({ task: response }));
  } catch (error) {
    yield* put(
      doTaskAssignedDetailsLoadFailure({
        error: isError(error) ? error.message : null,
      }),
    );
  }
}

export const tasksSideEffects = [
  takeLatest(doTasksAssignedLoad, loadTasks),
  takeLatest(doTasksAssignedDetailsLoad, loadTaskDetails),
  takeLatest(doTasksAssignedFilterOptionsLoad, loadTaskFilterOptions),
  takeLeading(doTasksAssignedFilterOpenModal, openTaskFilterModal),
];
